import { isDomTypingElement, isTextInputFocused } from "./player-input-gate.js";

/** Phaser scene plugin — toggles capture via manager.preventDefault. */
type KeyboardPluginLike = {
  disableGlobalCapture?: () => void;
  enableGlobalCapture?: () => void;
};

/** Phaser game.input.keyboard — global KeyboardManager. */
type KeyboardManagerLike = {
  preventDefault?: boolean;
};

type KeyboardTypingTarget = KeyboardPluginLike | KeyboardManagerLike;

let installed = false;
let captureDisabled = false;
let getKeyboard: (() => KeyboardTypingTarget | undefined) | null = null;

function isKeyboardPlugin(kb: KeyboardTypingTarget): kb is KeyboardPluginLike {
  return (
    typeof (kb as KeyboardPluginLike).disableGlobalCapture === "function" &&
    typeof (kb as KeyboardPluginLike).enableGlobalCapture === "function"
  );
}

function isKeyboardManager(kb: KeyboardTypingTarget): kb is KeyboardManagerLike {
  return typeof (kb as KeyboardManagerLike).preventDefault === "boolean";
}

function setGlobalCapture(enabled: boolean): void {
  const kb = getKeyboard?.();
  if (!kb) return;

  if (isKeyboardPlugin(kb)) {
    if (enabled) {
      if (captureDisabled) {
        kb.enableGlobalCapture!();
        captureDisabled = false;
      }
    } else if (!captureDisabled) {
      kb.disableGlobalCapture!();
      captureDisabled = true;
    }
    return;
  }

  if (isKeyboardManager(kb)) {
    const wantPrevent = enabled;
    if (kb.preventDefault === wantPrevent) {
      captureDisabled = !enabled;
      return;
    }
    kb.preventDefault = wantPrevent;
    captureDisabled = !enabled;
  }
}

function onFocusIn(e: FocusEvent): void {
  if (isDomTypingElement(e.target)) setGlobalCapture(false);
}

function onFocusOut(): void {
  requestAnimationFrame(() => {
    if (!isTextInputFocused()) setGlobalCapture(true);
  });
}

/**
 * While any DOM text field is focused, release Phaser's global key capture so
 * WASD / arrows / E reach the browser for normal typing.
 *
 * Pass `game.input.keyboard` (KeyboardManager) or a scene's `input.keyboard` plugin.
 */
export function wirePhaserKeyboardForDomTyping(
  keyboardGetter: () => KeyboardTypingTarget | undefined
): void {
  if (installed || typeof document === "undefined") return;
  getKeyboard = keyboardGetter;
  document.addEventListener("focusin", onFocusIn);
  document.addEventListener("focusout", onFocusOut);
  installed = true;
  if (isTextInputFocused()) setGlobalCapture(false);
}

/** Re-enable Phaser global key capture after overlay dismiss (e.g. narrative close). */
export function ensureGlobalCaptureEnabled(): void {
  setGlobalCapture(true);
}

/** Test-only reset. */
export function resetPhaserKeyboardTypingWire(): void {
  if (!installed || typeof document === "undefined") return;
  document.removeEventListener("focusin", onFocusIn);
  document.removeEventListener("focusout", onFocusOut);
  setGlobalCapture(true);
  installed = false;
  getKeyboard = null;
}
